import React from 'react';
import { Box, ButtonBase, InputAdornment, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import type { SxProps, TextFieldProps, Theme } from '@mui/material';
import { Check, Search, X } from 'lucide-react';
import { focusRing, sbg } from '../../theme/sbgTokens';

/* ------------------------------------------------------------------------ */
/* SearchField: the pill search input (book §11.4)                           */
/* ------------------------------------------------------------------------ */

export type SearchFieldProps = Omit<TextFieldProps, 'onChange' | 'value' | 'variant'> & {
  value: string;
  onChange: (value: string) => void;
  /** Accessible name; also the placeholder unless one is given. */
  label: string;
  width?: number | string;
  /** 'search' (default) exposes role searchbox; 'text' keeps role textbox. */
  type?: 'search' | 'text';
};

/**
 * A pill search field with a leading search icon and a clear button. Its
 * label is visually hidden (the icon and placeholder carry it) but announced.
 */
export const SearchField: React.FC<SearchFieldProps> = ({ value, onChange, label, placeholder, width = 280, type = 'search', sx, ...rest }) => (
  <TextField
    {...rest}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder ?? label}
    size="small"
    type={type}
    sx={{
      width: { xs: '100%', sm: width },
      '& .MuiOutlinedInput-root': { borderRadius: sbg.radius.pill, paddingLeft: '12px' },
      '& input::-webkit-search-cancel-button': { display: 'none' },
      ...(sx as object),
    }}
    slotProps={{
      htmlInput: { 'aria-label': label, ...((rest.slotProps as { htmlInput?: object } | undefined)?.htmlInput ?? {}) },
      input: {
        startAdornment: (
          <InputAdornment position="start">
            <Search size={16} aria-hidden="true" />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <ButtonBase
              aria-label="Clear search"
              onClick={() => onChange('')}
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                color: sbg.text.secondary,
                '&:hover': { bgcolor: sbg.bg.hover, color: sbg.text.primary },
                '&.Mui-focusVisible': focusRing,
              }}
            >
              <X size={14} />
            </ButtonBase>
          </InputAdornment>
        ) : undefined,
      },
    }}
  />
);

/* ------------------------------------------------------------------------ */
/* SegmentedControl (book §11.5): 2–5 options, switches a view, never submits */
/* ------------------------------------------------------------------------ */

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Accessible name when the label is icon-only. */
  'aria-label'?: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  size = 'medium',
  disabled = false,
  sx,
  ...rest
}: {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  size?: 'small' | 'medium';
  disabled?: boolean;
  'aria-label': string;
  sx?: SxProps<Theme>;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      size={size}
      value={value}
      onChange={(_, next: T | null) => {
        if (next !== null) onChange(next);
      }}
      aria-label={rest['aria-label']}
      disabled={disabled}
      sx={sx}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value} aria-label={option['aria-label']} disabled={option.disabled}>
          {option.icon}
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/* ------------------------------------------------------------------------ */
/* FilterChip (book §11.5): multi-select filter, calm when on               */
/* ------------------------------------------------------------------------ */

export const FilterChip: React.FC<{
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  count?: number;
  sx?: SxProps<Theme>;
}> = ({ selected, onClick, children, count, sx }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={selected}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      height: 32,
      px: 1.5,
      borderRadius: sbg.radius.pill,
      border: `1px solid ${selected ? sbg.status.info.border : sbg.border.default}`,
      bgcolor: selected ? sbg.status.info.bg : sbg.bg.surface,
      color: selected ? sbg.status.info.text : sbg.text.primary,
      font: 'var(--sbg-type-label-sm)',
      whiteSpace: 'nowrap',
      transition: `background-color ${sbg.duration.fast} ${sbg.ease.standard}`,
      '&:hover': { bgcolor: selected ? sbg.bg.selectedStrong : sbg.bg.hover },
      '&.Mui-focusVisible': focusRing,
      ...sx,
    }}
  >
    {selected && <Check size={14} aria-hidden="true" />}
    {children}
    {typeof count === 'number' && (
      <Box component="span" sx={{ color: selected ? sbg.status.info.text : sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
        {count}
      </Box>
    )}
  </ButtonBase>
);

/* ------------------------------------------------------------------------ */
/* Toolbar: the row above a table or list                                    */
/* ------------------------------------------------------------------------ */

export const Toolbar: React.FC<{ children: React.ReactNode; end?: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, end, sx }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', ...sx }}>
    {children}
    {end && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>{end}</Box>}
  </Box>
);

/* ------------------------------------------------------------------------ */
/* Kbd                                                                       */
/* ------------------------------------------------------------------------ */

export const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-grid',
      placeItems: 'center',
      minWidth: 20,
      height: 20,
      px: '5px',
      borderRadius: sbg.radius.sm,
      border: `1px solid ${sbg.border.default}`,
      bgcolor: sbg.bg.surfaceSubtle,
      color: sbg.text.secondary,
      font: `500 12px/16px ${sbg.font.body}`,
    }}
  >
    {children}
  </Box>
);
